import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow
} from "@mui/material";
import React, { useEffect, useState } from "react";
import JSONPretty from "react-json-pretty";
import { useParams } from "react-router-dom";
import { CachePolicies } from "use-http";

import { ErrorView } from "components/common/ErrorView";
import { ErrorType } from "components/common/ErrorView/types";
import { getConfig } from "config/config";
import { QUERY_NAMING } from "config/copies/query";
import { labelInternalToUI, labelMappings } from "config/LabelMapping";
import { useFetchCustom } from "hooks/useFetchCustom";
import { Typography } from "new-components/typography/Typography";
import { Detector } from "types/detectors";
import { ImageQuery } from "types/imageQueries";
import { UserProps } from "types/users";
import { generateImageUrl } from "utils/generateImageUrl";



const config = getConfig();

const { JANZU_URL } = config;

export function PosicheckInfoPage(props: UserProps) {
  const { user } = props;
  const { id } = useParams<{ id?: string }>();

  if (user) {
    if (id) {
      return <PosicheckInfo id={id} />;
    }
    return <Box>Please provide an id in the URL!</Box>;
  }
  return <Box>No user</Box>;
}

interface PosicheckInfoProps {
  id: string;
}

export function PosicheckInfo(props: PosicheckInfoProps) {
  const { id } = props;
  const [posicheck, setPosicheck] = useState<ImageQuery>();

  // Fetching ImageQuery
  const url = `${JANZU_URL}reef-api/image-queries/${id}`;
  const {
    data: IQFData,
    error: IQFError,
    loading: IQFLoading,
  } = useFetchCustom<ImageQuery>(url, { cachePolicy: CachePolicies.NO_CACHE }, []);

  // Fetching Detector
  const predictorUrl = `${JANZU_URL}reef-api/detectors/${IQFData?.predictor_id}`;
  const { get: getDetector, data: detectorData } = useFetchCustom<Detector>(predictorUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  useEffect(() => {
    if (!IQFError && IQFData) {
      setPosicheck(IQFData);
      // avoid fetching the detector if it's already fetched
      if (!detectorData) {
        getDetector();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IQFData, IQFError, getDetector]);

  // processing the fetched data
  const status = !detectorData || IQFError ? <CircularProgress size={25} /> : null;

  const iqId = IQFData?.iq_id;
  const labelPrompt = IQFData?.query;
  const className = labelInternalToUI(labelMappings, IQFData?.current_best_answer_str);
  const source = IQFData?.current_best_answer_source === "ALG" ? "Algorithm" : "Human";
  const originalPrediction = IQFData?.current_best_answer_source
    ? labelInternalToUI(labelMappings, IQFData?.current_best_answer_source)
    : "N/A";
  const originalConfidence = IQFData?.current_best_answer_confidence
    ? IQFData?.current_best_answer_confidence?.toFixed(4)
    : "N/A";

  if (!status && !posicheck) {
    return <ErrorView error={ErrorType.ERROR_404} />;
  }

  return (
    <Container>
      <Stack spacing={1}>
        {IQFLoading ? (
          <CircularProgress size={25} />
        ) : (
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Box display="flex" justifyContent="center" alignItems="center" height="250">
                      {!iqId
                        ? <CircularProgress size={25} /> 
                        : <img alt={`${iqId}`} src={generateImageUrl(iqId)} height="250" />
                      }
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>{`${QUERY_NAMING.uiName} ID`}</TableCell>
                        <TableCell>{id}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Query</TableCell>
                        <TableCell>{labelPrompt}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Answer</TableCell>
                        <TableCell>{className}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Source</TableCell>
                        <TableCell>{source}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Original Prediction</TableCell>
                        <TableCell>{originalPrediction}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Original Confidence</TableCell>
                        <TableCell>{originalConfidence}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Full JSON Data</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <JSONPretty data={IQFData} />
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Container>
  );
}
